import * as React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useAtomValue } from 'jotai';

import { getServerTranslations } from '../../get-server-side-translations';
import { useAuth } from '@restworld/feature-authentication';
import { orgOnboardingExistingUserFlowAtom } from '../../components/organization-onboarding/use-organization-onboarding';

const OrganizationOnboarding = dynamic(() => import('../../components/organization-onboarding/onboarding'), {
  ssr: false,
});

const NewEmployersPage: React.FunctionComponent = () => {
  const router = useRouter();
  const { isAuthenticated, isInitialized } = useAuth();
  const isExistingUserFlow = useAtomValue(orgOnboardingExistingUserFlowAtom);

  // Track whether user was authenticated when page first loaded
  // This prevents redirecting users who become authenticated during the onboarding flow
  const wasAuthenticatedOnMount = React.useRef<boolean | null>(null);

  // Capture authentication state on initial load
  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === null) {
      wasAuthenticatedOnMount.current = isAuthenticated;
    }
  }, [isAuthenticated, isInitialized]);

  // Only redirect users who were already logged in when they arrived at this page
  // Don't redirect users who become authenticated during the org onboarding flow
  // EXCEPTION: If the existing user flow flag is set (user logged in from org onboarding),
  // skip the redirect so we can create entities with their onboarding atom data
  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === true && !isExistingUserFlow) {
      router.replace('/employer/homepage');
    }
  }, [isInitialized, router, isExistingUserFlow]);

  // Don't render onboarding if user was already authenticated on mount
  // (unless they're returning from the existing user login flow)
  if (wasAuthenticatedOnMount.current === true && !isExistingUserFlow) {
    return null;
  }

  return (
    <>
      <Head>
        <title>Inizia con Restworld - Onboarding Nuovi Employer</title>
        <meta
          name="description"
          content="Crea il tuo locale, pubblica un annuncio di lavoro e inizia a trovare il personale perfetto con Restworld."
        />
        <meta property="og:title" content="Inizia con Restworld - Onboarding Nuovi Employer" />
        <meta
          property="og:description"
          content="Crea il tuo locale, pubblica un annuncio di lavoro e inizia a trovare il personale perfetto con Restworld."
        />
        <meta
          property="og:image"
          content="https://restworld-assets.s3.eu-west-1.amazonaws.com/showcase-getting-started.png"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Inizia con Restworld - Onboarding Nuovi Employer" />
        <meta
          name="twitter:description"
          content="Crea il tuo locale, pubblica un annuncio di lavoro e inizia a trovare il personale perfetto con Restworld."
        />
        <meta
          name="twitter:image"
          content="https://restworld-assets.s3.eu-west-1.amazonaws.com/showcase-getting-started.png"
        />
      </Head>
      <OrganizationOnboarding />
    </>
  );
};

export default NewEmployersPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await getServerTranslations(locale, [
    'common',
    'jp-onboarding',
    'restaurant-onboarding',
    'job-positions-page',
    'job-position-page',
    'payment-and-invoices',
  ]);
  return {
    props: {
      ...translation,
    },
  };
};
